import {
  ChangeEvent,
  FocusEvent,
  HTMLInputTypeAttribute,
  KeyboardEvent,
} from 'react';

import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { FieldGroup } from '@/components/shared/form/field-group';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { styled } from '@/stitches.config';
import { IIcon, IThemeLCV } from '@/types/shared';

const TextFieldBg = styled(Box, {
  pe: 'none',
  br: 'var(--input-rounded)',
  bc: 'var(--input-background-color)',
  bs: 'inset 0px 0px 0px 1px var(--input-stoke-color)',
  transitionProperty: 'box-shadow, background-color, color',
  transitionDelay: 'var(--transition-delay)',
  transitionDuration: 'var(--transition-duration)',
  transitionTimingFunction: 'var(--transition-easing)',
});

export const TextFieldWrapper = styled(Flex, {
  position: 'relative',
  width: '100%',
  px: 'var(--input-padding-inline)',
});

const StyledTextField = styled('input', {
  ap: 'none',
  border: 'none',
  outline: 'none',
  margin: '$space-0',
  padding: '$space-0',
  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
  position: 'relative',
  width: '100%',
  height: 'var(--input-height)',
  zIndex: 1,
  bc: '$transparent',
  color: 'var(--input-color)',
  fontFamily: '$regular',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 'normal',
  lineHeight: 'var(--input-height)',
  whiteSpace: 'nowrap',
  '&[type="number"]': {
    ap: 'textfield',
    '&::-webkit-inner-spin-button': {
      display: 'none',
    },
  },
  '&::placeholder': {
    color: 'var(--input-color-placeholder)',
  },
  '&:-webkit-autofill': {
    '-webkit-background-clip': 'text',
    '-webkit-text-fill-color': 'var(--input-color)',
  },
  '&:hover, &:focus': {
    [`& ~ ${TextFieldBg}`]: {
      bs: 'inset 0px 0px 0px 1px var(--input-stoke-color-focus)',
    },
  },
  '&:disabled': {
    opacity: '1',
    pe: 'none',
    cursor: 'not-allowed',
    us: 'none',
    color: 'var(--input-color-disabled)',
    [`& ~ ${TextFieldBg}`]: {
      bc: 'var(--input-background-color-disabled)',
      bs: 'inset 0px 0px 0px 1px var(--input-stoke-color-disabled)',
    },
    '&::placeholder': {
      color: 'var(--input-color-disabled)',
    },
    '&:-webkit-autofill': {
      '-webkit-text-fill-color': 'var(--input-color-disabled)',
    },
    '*': {
      us: 'none',
    },
  },
  variants: {
    theme: {
      light: {},
      dark: {
        color: 'var(--input-color-dark)',
        '&::placeholder': {
          color: 'var(--input-color-placeholder-dark)',
        },
        '&:-webkit-autofill': {
          '-webkit-text-fill-color': 'var(--input-color-dark)',
        },
        [`& ~ ${TextFieldBg}`]: {
          bc: 'var(--input-background-color-dark)',
          bs: 'inset 0px 0px 0px 1px var(--input-stoke-color-dark)',
        },
        '&:hover, &:focus': {
          [`& ~ ${TextFieldBg}`]: {
            bs: 'inset 0px 0px 0px 1px var(--input-stoke-color-focus-dark)',
          },
        },
        '&:disabled': {
          color: 'var(--input-color-disabled-dark)',
          [`& ~ ${TextFieldBg}`]: {
            bc: 'var(--input-background-color-disabled-dark)',
            bs: 'inset 0px 0px 0px 1px var(--input-stoke-color-disabled-dark)',
          },
        },
      },
    },
    error: {
      true: {
        color: 'var(--input-color-error)',
        '&::placeholder': {
          color: 'var(--input-color-error)',
        },
        '&:-webkit-autofill': {
          '-webkit-text-fill-color': 'var(--input-color-error)',
        },
        [`& ~ ${TextFieldBg}`]: {
          bs: 'inset 0px 0px 0px 1px var(--input-stoke-color-error)',
        },
        '&:hover, &:focus': {
          [`& ~ ${TextFieldBg}`]: {
            bs: 'inset 0px 0px 0px 1px var(--input-stoke-color-error)',
          },
        },
      },
    },
  },
  defaultVariants: {
    theme: 'light',
  },
});

export const TextFieldIcon = styled(Box, {
  fontSize: '0',
});

const SpinButton = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  size: '$size-6',
  height: '100%',
  zIndex: 1,
  '& > *': {
    w: '$size-6',
    h: '$size-4',
  },
  '& i': {
    marginTop: '-$space-2',
    cursor: 'pointer',
    '&:active, &:hover': {
      color: 'var(--input-color)',
    },
  },
});

const SpinButtonBox = styled(Box, {
  variants: {
    disabled: {
      true: {
        pe: 'none',
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
interface IProps extends React.ComponentProps<typeof StyledTextField> {
  ref?: React.Ref<HTMLInputElement>;
  autoComplete?: string;
  name: string;
  type?: HTMLInputTypeAttribute;
  placeholder?: string;
  pattern?: string;
  inputMode?:
    | 'none'
    | 'text'
    | 'search'
    | 'email'
    | 'tel'
    | 'url'
    | 'numeric'
    | 'decimal';
  value?: string;
  max?: string;
  maxLength?: number;
  min?: string;
  minLength?: number;
  readOnly?: boolean;
  disabled?: boolean;
  enterKeyHint?:
    | 'enter'
    | 'done'
    | 'go'
    | 'next'
    | 'previous'
    | 'search'
    | 'send';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  dataTest?: string;
  icon?: IIcon;
  iconDirection?: 'left' | 'right';
  prefix?: string;
  suffix?: string;
  spinButton?: boolean;
  spinbuttoniconupdisabled?: boolean;
  spinButtonIconUpOnClick?: () => void;
  spinbuttonicondowndisabled?: boolean;
  spinButtonIconDownOnClick?: () => void;
  theme?: IThemeLCV;
  // htmlFor?: string;
  label?: string;
  secondaryLabel?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  message?: string;
  css?: any;
  className?: string;
  // test?: ITest;
}

export const TextField: React.FC<IProps> = ({
  onChange,
  onBlur,
  onFocus,
  onKeyPress,
  icon,
  iconDirection,
  prefix,
  suffix,
  spinButton,
  spinButtonIconUpOnClick,
  spinButtonIconDownOnClick,
  spinbuttoniconupdisabled = false,
  spinbuttonicondowndisabled = false,
  label,
  secondaryLabel,
  required = false,
  error = false,
  errorMessage,
  message,
  css,
  className,
  type = 'text',
  theme = 'light',
  ...props
}) => {
  const _onChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(e);
  };

  const _onBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    onBlur && onBlur(e);
  };

  const _onFocus = (e: FocusEvent<HTMLInputElement, Element>) => {
    onFocus && onFocus(e);
  };

  const _onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyPress && onKeyPress(e);
  };

  const DISABLED_ICON_COLOR =
    theme === 'dark' ? 'icon-gray-light' : 'icon-disabled';
  const ERROR_ICON_COLOR = 'icon-red-200';
  const DEFAULT_ICON_COLOR =
    theme === 'dark' ? 'icon-disabled' : 'icon-gray-light';

  const DISABLED_TEXT_COLOR = theme === 'dark' ? 'text-gray' : 'text-disabled';
  const ERROR_TEXT_COLOR = 'text-red-200';
  const DEFAULT_TEXT_COLOR = theme === 'dark' ? 'text-disabled' : 'text-gray';

  return (
    <FieldGroup
      htmlFor={props?.name}
      label={label}
      secondaryLabel={secondaryLabel}
      required={required}
      error={error}
      errorMessage={errorMessage}
      message={message}
      css={css}
      className={className}
      theme={theme}
      test={{
        dataTest: props?.name,
      }}
    >
      <TextFieldWrapper alignItems="center" columnGap="2">
        {!!icon && iconDirection !== 'right' && (
          <TextFieldIcon position="relative" zIndex="1">
            <Icon
              icon={icon}
              color={
                props.disabled
                  ? DISABLED_ICON_COLOR
                  : error
                    ? ERROR_ICON_COLOR
                    : DEFAULT_ICON_COLOR
              }
            />
          </TextFieldIcon>
        )}
        {!!prefix && (
          <Box position="relative" zIndex="1" userSelect="none">
            <Text
              variant={
                props?.disabled
                  ? DISABLED_TEXT_COLOR
                  : error
                    ? ERROR_TEXT_COLOR
                    : DEFAULT_TEXT_COLOR
              }
              data-test={`input_prefix_${props?.name}`}
            >
              {prefix}
            </Text>
          </Box>
        )}
        <StyledTextField
          {...props}
          id={props.id ? props.id : props.name}
          onChange={_onChange}
          onBlur={_onBlur}
          onFocus={_onFocus}
          onKeyPress={_onKeyPress}
          error={error}
          type={type}
          data-test={`input_` + props?.name}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          theme={theme}
        />
        {!!icon && iconDirection === 'right' && (
          <TextFieldIcon position="relative" zIndex="1">
            <Icon
              icon={icon}
              color={
                props.disabled
                  ? DISABLED_ICON_COLOR
                  : error
                    ? ERROR_ICON_COLOR
                    : DEFAULT_ICON_COLOR
              }
            />
          </TextFieldIcon>
        )}
        {!!suffix && (
          <Box position="relative" zIndex="1" userSelect="none">
            <Text
              variant={
                props?.disabled
                  ? DISABLED_TEXT_COLOR
                  : error
                    ? ERROR_TEXT_COLOR
                    : DEFAULT_TEXT_COLOR
              }
              data-test={`input_suffix_${props?.name}`}
            >
              {suffix}
            </Text>
          </Box>
        )}
        {!!spinButton && (
          <SpinButton>
            <SpinButtonBox
              overflow="hidden"
              onClick={spinButtonIconUpOnClick}
              test={{ dataTest: 'button_increase' }}
              disabled={spinbuttoniconupdisabled || props.disabled}
            >
              <Icon
                icon="arrow-drop-up"
                color={
                  spinbuttoniconupdisabled || props.disabled
                    ? DISABLED_ICON_COLOR
                    : error
                      ? ERROR_ICON_COLOR
                      : DEFAULT_ICON_COLOR
                }
              />
            </SpinButtonBox>
            <SpinButtonBox
              overflow="hidden"
              onClick={spinButtonIconDownOnClick}
              test={{ dataTest: 'button_decrease' }}
              disabled={spinbuttonicondowndisabled || props.disabled}
            >
              <Icon
                icon="arrow-drop-down"
                color={
                  spinbuttonicondowndisabled || props.disabled
                    ? DISABLED_ICON_COLOR
                    : error
                      ? ERROR_ICON_COLOR
                      : DEFAULT_ICON_COLOR
                }
              />
            </SpinButtonBox>
          </SpinButton>
        )}

        <TextFieldBg position="absolute" inset="0" />
      </TextFieldWrapper>
    </FieldGroup>
  );
};

TextField.displayName = 'TextField';
